import React, { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';

// Generic email-template editor -- not specific to transfer requests or
// any other feature. Each template is plain text (like every other
// admin-editable content field in this app) with $placeholder tokens,
// substituted per-recipient wherever a feature actually sends it.
const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/email-templates`);
      const data = await res.json();
      if (res.ok) {
        setTemplates(data.templates || []);
      } else {
        setError(data.error || 'Failed to load email templates.');
      }
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (t) => {
    setEditingKey(t.key);
    setForm({ subject: t.subject, body: t.body });
    setMessage('');
    setError('');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setForm({ subject: '', body: '' });
  };

  const save = async (key) => {
    if (!form.subject.trim() || !form.body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/email-templates/${key}`, {
        method: 'PUT', body: JSON.stringify({ subject: form.subject, body: form.body }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTemplates(prev => prev.map(t => t.key === key ? data.template : t));
        setMessage('Saved.');
        cancelEdit();
      } else {
        setError(data.error || 'Failed to save template.');
      }
    } catch {
      setError('Network error.');
    }
    setSaving(false);
  };

  if (loading) return <p style={{ padding: '24px' }}>Loading…</p>;

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <h2 style={{ marginTop: 0 }}>Email Templates</h2>
      <p style={{ color: '#64748b', fontSize: '13px' }}>
        Subject and body support <code>$placeholder</code> tokens, filled in per recipient when a feature sends this
        template (e.g. approving a transfer request). Body is plain text, not HTML — line breaks are preserved.
      </p>

      {error && <div className="error-banner">{error}</div>}
      {message && <p style={{ color: '#166534', fontSize: '13px' }}>{message}</p>}

      {templates.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>No email templates yet.</p>
      ) : (
        templates.map(t => (
          <div className="form-card" key={t.key} style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{t.label}</h3>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Key: {t.key}</span>
              </div>
              {editingKey !== t.key && (
                <button className="btn-sm" onClick={() => startEdit(t)}>Edit</button>
              )}
            </div>

            {t.available_placeholders && (
              <p style={{ fontSize: '12px', color: '#475569', marginTop: '8px' }}>
                Available placeholders: {t.available_placeholders.split(',').map(p => (
                  <code key={p.trim()} style={{ marginRight: '6px' }}>${p.trim()}</code>
                ))}
              </p>
            )}

            {editingKey === t.key ? (
              <>
                <div className="form-row" style={{ marginTop: '12px' }}>
                  <label>Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="form-row">
                  <label>Body</label>
                  <textarea
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    rows={8}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <button className="btn-primary" onClick={() => save(t.key)} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>{' '}
                <button className="btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
              </>
            ) : (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Subject:</strong> {t.subject}</div>
                <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px' }}>
                  {t.body}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AdminEmailTemplates;
